import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/lac/Navbar";
import Footer from "../components/lac/Footer";
import { api, formatApiError } from "../lib/api";

const inputCls = "w-full bg-[#0d0d0d] border border-white/10 rounded-sm px-4 py-3 text-white focus:border-[#D946EF] focus:outline-none focus:ring-1 focus:ring-[#D946EF]/30 transition";

export default function GallerySubmit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking") || null;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const canSubmit = name.trim().length >= 2 && email.includes("@") && mediaUrl.trim().startsWith("http");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/gallery/submit", {
        name: name.trim(),
        email: email.trim(),
        vehicle: vehicle.trim(),
        media_url: mediaUrl.trim(),
        note: note.trim(),
        booking_id: bookingId,
      });
      setDone(true);
      toast.success("Submitted! We'll review and feature you shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 pt-32 pb-24" data-testid="gallery-submit-page">
        <Link to="/gallery" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8 transition" data-testid="gallery-submit-back">
          <ArrowLeft className="w-4 h-4" /> Back to gallery
        </Link>

        {done ? (
          <div data-testid="gallery-submit-success" className="p-10 rounded-sm border border-[#D946EF]/40 bg-gradient-to-br from-[#D946EF]/10 to-[#FF2D87]/10 text-center">
            <div className="w-14 h-14 rounded-full bg-[#D946EF] flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-white" strokeWidth={3} />
            </div>
            <h2 className="font-display font-bold text-3xl tracking-tight">Thanks — got it!</h2>
            <p className="text-zinc-300 mt-3 font-body leading-relaxed">
              We'll have a look and feature you in the gallery shortly. Keep an eye on
              <a href="https://www.tiktok.com/@lac.ltd" target="_blank" rel="noopener noreferrer" className="text-[#D946EF] hover:underline ml-1">@lac.ltd</a> — we tag every customer we post.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/gallery" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D946EF] text-white font-semibold text-sm hover:bg-[#F0ABFC] transition">View gallery</Link>
              <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:border-[#D946EF]/60 transition text-sm">Home</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs tracking-[0.35em] text-[#D946EF] mb-3">SUBMIT YOUR POV</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">
              Send us <span className="neon-violet">your install.</span>
            </h1>
            <p className="text-zinc-400 mt-4 font-body leading-relaxed">
              Photos, a TikTok link, an Instagram reel — paste it below and we'll feature you in the LAC customer gallery and tag you on our channels.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="gallery-submit-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm text-zinc-300 mb-2 block" htmlFor="g-name">Your name</label>
                  <input id="g-name" data-testid="g-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="First & last" className={inputCls} />
                </div>
                <div>
                  <label className="text-sm text-zinc-300 mb-2 block" htmlFor="g-email">Email</label>
                  <input id="g-email" type="email" data-testid="g-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
                </div>
              </div>

              <div>
                <label className="text-sm text-zinc-300 mb-2 block" htmlFor="g-vehicle">Your car (optional)</label>
                <input id="g-vehicle" data-testid="g-vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="e.g. 2022 BMW M3 Competition" className={inputCls} />
              </div>

              <div>
                <label className="text-sm text-zinc-300 mb-2 block" htmlFor="g-media">Photo / Video link</label>
                <input id="g-media" data-testid="g-media-url" value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} placeholder="https://tiktok.com/… · https://instagram.com/… · direct image URL" className={inputCls} />
                <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                  Paste a TikTok / Instagram / YouTube link, or a hosted image URL (Google Photos shared link, iCloud Drive link, etc).
                </p>
              </div>

              <div>
                <label className="text-sm text-zinc-300 mb-2 block" htmlFor="g-note">A few words (optional)</label>
                <textarea id="g-note" data-testid="g-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="How was the experience? Any details we should highlight?" className={`${inputCls} resize-none`} />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={!canSubmit || submitting}
                  data-testid="g-submit"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D946EF] text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#F0ABFC] transition"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send my POV
                </button>
              </div>
            </form>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
